import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'

import {
  fetchTagsAndSegmentsError,
  fetchTagsAndSegmentsSuccess,
  getLanguagesError,
  getLanguagesSuccess,
  updateLanguagesError,
  updateLanguagesSuccess,
} from './actions'
import {
  LANGUAGES_REQUEST_ASYNC,
  TAGS_AND_SEGMENTS_REQUEST_ASYNC,
  UPDATE_LANGUAGES_ASYNC,
} from './constants'
import { selectLanguages, selectSaveLanguageFromTags, selectTagsAndSegments } from './selectors'

const getLanguagesSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS, LANGUAGES_REQUEST_ASYNC.INITIATE),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.languages)
      return true
    }),
    mergeMap(() =>
      api.settings.getLanguages(state$.value.partnerOptions.selectedCompany?.value, true).pipe(
        map((response) => getLanguagesSuccess(response)),
        catchError((error) => of(epicsErrorHandler(getLanguagesError, error))),
        takeUntil(action$.pipe(ofType(LANGUAGES_REQUEST_ASYNC.CANCEL)))
      )
    )
  )

const updateLanguagesSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_LANGUAGES_ASYNC.INITIATE),
    mergeMap(() => {
      const state = state$.value
      const companyId = state.partnerOptions.selectedCompany?.value
      const languages = selectLanguages(state)
      const defaultLanguage = languages.find((language) => language.default).id
      const saveLanguageFromTags = selectSaveLanguageFromTags(state)
      const selectedLanguages = languages
        .filter((language) => language.enabled)
        .map((language) => language.id)
      const associatedLanguages = languages
        .filter((language) => language.associate)
        .map((language) => ({
          langId: language.id,
          attrType: language.associate.tag !== undefined ? 'tag' : 'segment',
          attrId: language.associate.tag || language.associate.segment,
        }))

      return api.settings
        .updateLanguages(companyId, {
          defaultLanguage,
          saveLanguageFromTags,
          selectedLanguages,
          associatedLanguages,
        })
        .pipe(
          map((response) => updateLanguagesSuccess(response)),
          catchError((error) => of(epicsErrorHandler(updateLanguagesError, error))),
          takeUntil(action$.pipe(ofType(UPDATE_LANGUAGES_ASYNC.CANCEL)))
        )
    })
  )

const fetchTagsAndSegmentsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS, TAGS_AND_SEGMENTS_REQUEST_ASYNC.INITIATE),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.languages)
      return true
    }),
    mergeMap(() =>
      api.mailboxes
        .getTagsAndSegments({
          companyId: state$.value.partnerOptions.selectedCompany?.value,
          page: selectTagsAndSegments(state$.value).page,
          search: selectTagsAndSegments(state$.value).search,
        })
        .pipe(
          map((response) => fetchTagsAndSegmentsSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchTagsAndSegmentsError, error))),
          takeUntil(action$.pipe(ofType(TAGS_AND_SEGMENTS_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpics(
  getLanguagesSettingsEpic,
  updateLanguagesSettingsEpic,
  fetchTagsAndSegmentsEpic
)
